import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  SAIL_MANOEUVRES,
  type ManoeuvreStep,
  type SailManoeuvre,
} from "@nav-games/sailing";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface SequencesProps {
  onNavigate: (to: string) => void;
}

const MODULE = "sailing";
const GAME = "sequences";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  manoeuvre: SailManoeuvre;
  /// Index of the step we're asking about — the student sees the
  /// preceding steps and picks the next.
  stepIndex: number;
  /// The correct next step.
  answer: ManoeuvreStep;
  choices: readonly ManoeuvreStep[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

function buildCard(): Card {
  const manoeuvre =
    SAIL_MANOEUVRES[randInt(0, SAIL_MANOEUVRES.length - 1)]!;
  // Pick a step from somewhere in the middle so there's context
  // before AND after. Allow the first step (cold start) on rare
  // occasions for variety.
  const stepIndex = randInt(
    Math.min(1, manoeuvre.steps.length - 1),
    manoeuvre.steps.length - 1,
  );
  const answer = manoeuvre.steps[stepIndex]!;

  // Distractors: random steps from OTHER manoeuvres, plus one or
  // two later steps from the same manoeuvre (to test "right
  // sequence, wrong order").
  const others: ManoeuvreStep[] = [];
  for (const other of SAIL_MANOEUVRES) {
    if (other.id === manoeuvre.id) continue;
    for (const s of other.steps) others.push(s);
  }
  const sameMan: ManoeuvreStep[] = manoeuvre.steps.filter(
    (s, i) => i !== stepIndex,
  );
  const pool = shuffled([...others, ...sameMan]).filter(
    (s) => s.action !== answer.action,
  );
  const distractors = pool.slice(0, CHOICES_PER_CARD - 1);
  const choices = shuffled([answer, ...distractors]);
  return { manoeuvre, stepIndex, answer, choices };
}

export function Sequences({ onNavigate }: SequencesProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (action: string) => {
      if (chosen || result) return;
      const ok = action === card.answer.action;
      const attempt: Omit<Attempt, "module"> = {
        char: `${card.manoeuvre.id}-${card.stepIndex}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(action);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/sailing")}
      />
    );
  }

  const revealed = chosen !== null;
  const m = card.manoeuvre;
  const priorSteps = m.steps.slice(0, card.stepIndex);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "sailing" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          {m.name} — what happens next?
        </h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          {m.summary}
        </p>

        {priorSteps.length > 0 && (
          <div
            style={{
              padding: 10,
              marginBottom: 12,
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <p className="small muted" style={{ margin: "0 0 4px" }}>
              So far:
            </p>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {priorSteps.map((s, i) => (
                <li key={i} className="small" style={{ marginBottom: 4 }}>
                  <ActorTag actor={s.actor} />
                  {s.callout && (
                    <span style={{ fontStyle: "italic" }}>
                      &nbsp;&ldquo;{s.callout}&rdquo; —
                    </span>
                  )}{" "}
                  {s.action}
                </li>
              ))}
            </ol>
          </div>
        )}

        <p style={{ marginBottom: 12, fontWeight: 600 }}>
          Step {card.stepIndex + 1} of {m.steps.length} — what happens?
        </p>

        <div style={{ display: "grid", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.action === card.answer.action;
            const isChosen = c.action === chosen;
            return (
              <button
                key={c.action}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.action)}
                style={{
                  textAlign: "left",
                  fontSize: 14,
                  padding: "10px 12px",
                  fontWeight: 500,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                <ActorTag actor={c.actor} />
                {c.callout && (
                  <span style={{ fontStyle: "italic" }}>
                    &nbsp;&ldquo;{c.callout}&rdquo; —
                  </span>
                )}{" "}
                {c.action}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answer.action
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answer.action
                ? `✓ Correct.`
                : `✗ Not quite — the next step is:`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              <ActorTag actor={card.answer.actor} />
              {card.answer.callout && (
                <span style={{ fontStyle: "italic" }}>
                  &nbsp;&ldquo;{card.answer.callout}&rdquo; —
                </span>
              )}{" "}
              {card.answer.action}
            </p>
            {card.answer.reason && (
              <p
                className="small muted"
                style={{ margin: "6px 0 0", fontStyle: "italic" }}
              >
                <strong>Why:</strong> {card.answer.reason}
              </p>
            )}
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/sailing")}>
        ← Back to Sailing
      </button>
    </>
  );
}

function ActorTag({ actor }: { actor: ManoeuvreStep["actor"] }) {
  const label = actor === "helm" ? "Helm" : actor === "crew" ? "Crew" : "Both";
  const colour =
    actor === "helm" ? "#1e6091" : actor === "crew" ? "#c08428" : "#555";
  return (
    <span
      style={{
        background: colour,
        color: "white",
        borderRadius: 3,
        padding: "1px 6px",
        fontSize: 11,
        fontWeight: 600,
        marginRight: 4,
        display: "inline-block",
      }}
    >
      {label}
    </span>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Sailing
        </button>
      </div>
    </>
  );
}
